/**
 * Storage Bridge
 * 
 * Provides storage information.
 * Partial support via StorageManager API (Chrome/Edge only).
 */

import { BaseBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities, StorageInfo } from '../types'

export class StorageBridge extends BaseBridge {
  readonly bridgeName = 'Storage'
  readonly availability: BridgeAvailability = 'partial'
  readonly methods = ['getStorageInfo']
  
  private supported = false
  
  async initialize(): Promise<void> {
    this.supported = 'storage' in navigator && 'estimate' in navigator.storage
  }
  
  async handleCall(method: string, _args: any[]): Promise<any> {
    switch (method) {
      case 'getStorageInfo':
        return JSON.stringify(await this.getStorageInfo())
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private async getStorageInfo(): Promise<StorageInfo> {
    if (!this.supported) {
      // Return fallback for unsupported browsers
      return {
        quota: 0,
        usage: 0,
        available: 0,
        persistent: false
      }
    }
    
    try {
      const estimate = await navigator.storage.estimate()
      const persisted = await navigator.storage.persisted()
      
      const quota = estimate.quota || 0
      const usage = estimate.usage || 0
      
      return {
        quota,
        usage,
        available: quota - usage,
        persistent: persisted
      }
    } catch {
      return {
        quota: 0,
        usage: 0,
        available: 0,
        persistent: false
      }
    }
  }
  
  getCapabilities(): BridgeCapabilities {
    return {
      getStorageInfo: { 
        available: true,
        fallback: this.supported ? undefined : 'Returns zeros (API not supported)'
      }
    }
  }
}

